import {getTrackReferenceId, isEqualTrackRef, isTrackReference, isWeb, log, setupParticipantName} from '@livekit/components-core';
import {RoomEvent, Track} from 'livekit-client';
import * as React from 'react';
import {
    CarouselLayout,
    ConnectionStateToast,
    FocusLayout,
    FocusLayoutContainer,
    GridLayout,
    LayoutContextProvider,
    ParticipantTile,
    RoomAudioRenderer,
    useCreateLayoutContext,
    usePinnedTracks,
    useTracks,
    Chat, useEnsureParticipant, useLocalParticipant, useEnsureTrackRef,
} from "@livekit/components-react";
import './videoConference.css';
import {CustomParticipantTile} from "./CustomParticipantTile";
import {CustomVideoTrack} from "./CustomVideoTrack";

export function NewCustomVideoConference({
                                             recordingInProgress,
                                             startRecording,
                                             setRecordingDisabled,
                                             stopRecording,
                                             localRecordingInProgress,
                                             nameStyle,
                                             videoMirrored,
                                             hostIdentity,
                                             themeBackgroundColor,
                                             brandLogoUrl,
                                             chatMessageFormatter,
                                             chatMessageDecoder,
                                             chatMessageEncoder,
                                             isSoloSession = false,
                                             ...props
                                         }) {
    const [widgetState, setWidgetState] = React.useState({
        showChat: false,
        unreadMessages: 0,
    });
    const lastAutoFocusedScreenShareTrack = React.useRef(null);

    const tracks = useTracks(
        [
            {source: Track.Source.Camera, withPlaceholder: true},
            {source: Track.Source.ScreenShare, withPlaceholder: false},
        ],
        {updateOnlyOn: [RoomEvent.ActiveSpeakersChanged], onlySubscribed: false},
    );

    const internalAgentParticipants = ["TimerAgent", "RecordingAgent", "Soundboard"];
    const {cameraTrack, microphoneTrack} = useLocalParticipant();

    const hasLocalVideo = cameraTrack && !cameraTrack.isMuted;
    const hasLocalAudio = microphoneTrack && !microphoneTrack.isMuted;

    const localScreenShareTrack = tracks.find(
        (t) => t.participant.isLocal && t.publication?.source === Track.Source.ScreenShare,
    );

    const overlayCameraOnScreenShare =
        isSoloSession && !!localScreenShareTrack && hasLocalVideo;

    let visibleTracks = tracks.filter((track) => !internalAgentParticipants.includes(track.participant.identity));

    if (
        isSoloSession &&
        (!(hasLocalVideo || hasLocalAudio) || !!localScreenShareTrack)
    ) {
        visibleTracks = visibleTracks.filter(
            (track) => !(track.participant.isLocal && track.publication?.source === Track.Source.Camera),
        );
    }

    const localCameraTrackRef = tracks.find(
        (t) => t.participant.isLocal && t.publication?.source === Track.Source.Camera,
    );

    // LiveKit's visual-stability pagination can sometimes throw during fast track id changes
    // (e.g. camera trackSid changes or placeholder replacement). Force a remount of the
    // layout components when the set of track ids changes to avoid disconnect/crash loops.
    const visibleTracksKey = visibleTracks
        .map((t) => {
            try {
                return getTrackReferenceId(t);
            } catch {
                return '';
            }
        })
        .filter(Boolean)
        .sort()
        .join('|');

    const widgetUpdate = (state) => {
        log.debug('updating widget state', state);
        setWidgetState(state);
    };

    const layoutContext = useCreateLayoutContext();
    const focusLayoutWrapperRef = React.useRef(null);

    const screenShareTracks = tracks
        .filter(isTrackReference)
        .filter((track) => track.publication.source === Track.Source.ScreenShare);

    const focusTrack = usePinnedTracks(layoutContext)?.[0];
    const carouselTracks = visibleTracks.filter((track) => !isEqualTrackRef(track, focusTrack));
    const carouselTracksKey = carouselTracks
        .map((t) => {
            try {
                return getTrackReferenceId(t);
            } catch {
                return '';
            }
        })
        .filter(Boolean)
        .sort()
        .join('|');

    React.useLayoutEffect(() => {
        if (!focusTrack) return;
        const wrapper = focusLayoutWrapperRef.current;
        if (!wrapper) return;

        let raf = null;

        const ensureCarouselHasReasonableVisibleTiles = () => {
            const carouselEl = wrapper.querySelector?.('.lk-carousel');
            if (!carouselEl) return;

            const currentRaw =
                carouselEl.style.getPropertyValue('--lk-max-visible-tiles') ||
                window.getComputedStyle(carouselEl).getPropertyValue('--lk-max-visible-tiles');
            const current = Number.parseInt(String(currentRaw).trim(), 10);

            // Prevent the sporadic failure mode where the sidebar thinks only 1 tile can be visible,
            // causing a single (very tall) tile that hides the rest.
            if (current !== 1) return;

            const rect = carouselEl.getBoundingClientRect();
            if (!(rect.width > 0 && rect.height > 0)) return;

            // Mirror the LiveKit heuristic for vertical carousels (keep it dynamic; no fixed cap).
            const tileTargetHeight = Math.max(rect.width * 0.6, 130);
            const desired = Math.max(Math.round(rect.height / tileTargetHeight), 2);

            if (desired !== current) {
                carouselEl.style.setProperty('--lk-max-visible-tiles', String(desired));
            }
        };

        raf = window.requestAnimationFrame(ensureCarouselHasReasonableVisibleTiles);

        return () => {
            if (raf != null) {
                window.cancelAnimationFrame(raf);
            }
        };
    }, [focusTrack?.publication?.trackSid, carouselTracks.length]);

    const showBarVisualizer = hasLocalAudio && !hasLocalVideo && isSoloSession && screenShareTracks.length === 0 && visibleTracks.length === 1;

    React.useEffect(() => {
        // If screen share tracks are published, and no pin is set explicitly, auto set the screen share. Ignore in solo sessions.
        if (
            screenShareTracks.some((track) => track.publication.isSubscribed) &&
            lastAutoFocusedScreenShareTrack.current === null && !isSoloSession
        ) {
            log.debug('Auto set screen share focus:', {newScreenShareTrack: screenShareTracks[0]});
            layoutContext.pin.dispatch?.({msg: 'set_pin', trackReference: screenShareTracks[0]});
            lastAutoFocusedScreenShareTrack.current = screenShareTracks[0];
        } else if (
            lastAutoFocusedScreenShareTrack.current &&
            !screenShareTracks.some(
                (track) =>
                    track.publication.trackSid ===
                    lastAutoFocusedScreenShareTrack.current?.publication?.trackSid,
            )
        ) {
            log.debug('Auto clearing screen share focus.');
            layoutContext.pin.dispatch?.({msg: 'clear_pin'});
            lastAutoFocusedScreenShareTrack.current = null;
        }
    }, [
        screenShareTracks
            .map((ref) => `${ref.publication.trackSid}_${ref.publication.isSubscribed}`)
            .join(),
        focusTrack?.publication?.trackSid,
    ]);

    if (!visibleTracks.length && isSoloSession) {
        setRecordingDisabled(true);
        return (
            <div className="libretto-video-conference" style={{backgroundColor: themeBackgroundColor, justifyContent: "center", alignItems: "center"}} {...props}>
                <div style={{color: "#1a1a1a"}}>Please select an input below</div>
            </div>
        )
    }

    setRecordingDisabled(false);
    return (
        <div className="libretto-video-conference" style={{backgroundColor: themeBackgroundColor}} {...props}>
            {isWeb() && (
                <LayoutContextProvider
                    value={layoutContext}
                    // onPinChange={handleFocusStateChange}
                    onWidgetChange={widgetUpdate}
                >
                    <div className="libretto-video-conference-inner">
                        {!focusTrack ? (
                            <div className="libretto-grid-layout-wrapper">
                                <GridLayout key={`grid:${visibleTracksKey}`} tracks={visibleTracks}>
                                    <CustomParticipantTile nameStyle={nameStyle}
                                                           showBarVisualizer={showBarVisualizer}
                                                           hostIdentity={hostIdentity}
                                                           brandLogoUrl={brandLogoUrl}
                                                           videoMirrored={videoMirrored}
                                                           isSoloSession={isSoloSession}/>
                                </GridLayout>
                                {overlayCameraOnScreenShare && localCameraTrackRef && (
                                    <div className="libretto-camera-overlay">
                                        <CustomVideoTrack trackRef={localCameraTrackRef}
                                                          videoMirrored={videoMirrored}
                                                          isSoloSession={isSoloSession}/>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="libretto-focus-layout-wrapper" ref={focusLayoutWrapperRef}>
                                <FocusLayoutContainer>
                                    <CarouselLayout key={`carousel:${carouselTracksKey}`} tracks={carouselTracks} orientation="vertical">
                                        <CustomParticipantTile nameStyle={nameStyle}
                                                               showBarVisualizer={showBarVisualizer}
                                                               hostIdentity={hostIdentity}
                                                               brandLogoUrl={brandLogoUrl}
                                                               videoMirrored={videoMirrored}
                                                               isSoloSession={isSoloSession}/>
                                    </CarouselLayout>
                                    {focusTrack && <FocusLayout trackRef={focusTrack}/>}
                                    {overlayCameraOnScreenShare && localCameraTrackRef && (
                                        <div className="libretto-camera-overlay">
                                            <CustomVideoTrack trackRef={localCameraTrackRef}
                                                              videoMirrored={videoMirrored}
                                                              isSoloSession={isSoloSession}/>
                                        </div>
                                    )}
                                </FocusLayoutContainer>
                            </div>
                        )}
                    </div>
                </LayoutContextProvider>
            )}
            <RoomAudioRenderer/>
            <ConnectionStateToast/>
        </div>
    );
}
